import { apiClient } from './api';
import {
  DeliveryOption,
  DeliveryOptionCreate,
  DeliveryOptionListResponse,
} from '@/types';

/**
 * Product Delivery Options API Service
 * Handles CRUD operations for product-level and shop-wide delivery zones
 */
export const deliveryOptionApi = {
  /**
   * Get delivery options for a product
   * Returns product-specific options, shop defaults, and effective options
   */
  getProductDeliveryOptions: (productId: number) =>
    apiClient.get<DeliveryOptionListResponse>(`/products/${productId}/delivery-options`),

  /**
   * Get delivery options for a shop (shop-wide defaults)
   */
  getShopDeliveryOptions: (shopId: number) =>
    apiClient.get<DeliveryOption[]>(`/shops/${shopId}/delivery-options`),

  /**
   * Create a delivery option for a product
   * Requires seller to own the product
   */
  createProductDeliveryOption: (productId: number, data: DeliveryOptionCreate) =>
    apiClient.post<DeliveryOption>(`/products/${productId}/delivery-options`, data),

  /**
   * Create a shop-wide default delivery option
   * Requires seller to own the shop
   */
  createShopDeliveryOption: (shopId: number, data: DeliveryOptionCreate) =>
    apiClient.post<DeliveryOption>(`/shops/${shopId}/delivery-options`, data),

  /**
   * Update a delivery option
   */
  updateDeliveryOption: (productId: number, optionId: number, data: Partial<DeliveryOptionCreate>) =>
    apiClient.put<DeliveryOption>(`/products/${productId}/delivery-options/${optionId}`, data),

  /**
   * Delete a delivery option
   * Cannot delete if product has no other options unless force=true
   */
  deleteDeliveryOption: (productId: number, optionId: number, force: boolean = false) =>
    apiClient.delete(`/products/${productId}/delivery-options/${optionId}${force ? '?force=true' : ''}`),

  /**
   * Delete a product delivery option (alias for deleteDeliveryOption)
   * Used during bulk updates with force=true to allow deleting all options
   */
  deleteProductDeliveryOption: (productId: number, optionId: number) =>
    apiClient.delete(`/products/${productId}/delivery-options/${optionId}?force=true`),

  /**
   * Reorder delivery options by updating display_order
   */
  reorderDeliveryOptions: (productId: number, optionId: number, displayOrder: number) =>
    apiClient.put<DeliveryOption>(`/products/${productId}/delivery-options/${optionId}`, {
      display_order: displayOrder,
    }),
};
